// supabase-anon.ts
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/types/database";

/**
 * A Supabase client that never carries a session: every request it makes goes out as `anon`.
 *
 * Why it exists (owner decision, 2026-10-13): contact and rights-holder data are for fully set-up
 * accounts only. A signed-in account that is NOT fully set up reads no `tracks` rows at all through
 * its own session (backend migration 20261013055000_set_up_accounts_only.sql), so on the public
 * pages it reads as a signed-out visitor does: through this client, with the public columns
 * (`publicReadForViewer` in `public-catalog-columns.ts` decides which client a read uses).
 *
 * It never touches the app's session: nothing persisted (memory storage, so it never sees the
 * cookie or localStorage session `supabase.ts` keeps), no token refresh, no session read from the
 * URL, and a storage key of its own so its auth lock never waits on the main client's.
 *
 * Created on first use rather than on import: most visits are signed-in, set-up accounts that never
 * need it, and every extra auth client logs a warning in the browser.
 */
const ANON_STORAGE_KEY = "zen-anon-public-reads";

let client: SupabaseClient<Database> | null = null;

export function getAnonSupabase(): SupabaseClient<Database> {
  if (client) return client;

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error("Supabase configuration is missing. Check your .env file.");
  }

  client = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    db: { schema: "public" },
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
      storageKey: ANON_STORAGE_KEY,
    },
  });
  return client;
}
